import { CHECKLIST_TYPES, round2 } from "../../util/common";

const sortQuestionResults = questionItems => {
  return (
    questionItems
      ?.map(qr => ({ ...qr, pin_history: qr.pin_history?.toSorted((a, b) => b.id - a.id) ?? [] }))
      .sort((a, b) => {
        if (a.group_order === b.group_order) {
          if (a.question_order < b.question_order) return -1;
          if (a.question_order > b.question_order) return 1;
          if (a.id < b.id) return -1;
          if (a.id > b.id) return 1;
          return 0;
        }
        return a.group_order < b.group_order ? -1 : 1;
      }) ?? []
  );
};

const groupQuestionResults = questionItems => {
  const grouped = new Map();
  if (!questionItems) return grouped;

  questionItems.forEach(qr => {
    const group = grouped.get(qr.group_name);
    if (group) group.push(qr);
    else grouped.set(qr.group_name, [qr]);
  });

  return grouped;
};

const orderAndGroupChecks = checks => {
  if (!checks) return checks;

  const extraPartChecks = [];
  const otherChecks = [];

  checks.forEach(check => (check.checklist?.checklist_type === CHECKLIST_TYPES.ExtraParts ? extraPartChecks.push(check) : otherChecks.push(check)));

  extraPartChecks.sort((a, b) => b.id - a.id);
  otherChecks.sort((a, b) => a.id - b.id);

  const orderedChecksWithOrderedQuestions = (extraPartChecks.length > 0 ? [extraPartChecks[0], ...otherChecks] : otherChecks).map(check => ({
    ...check,
    question_items: sortQuestionResults(check.question_items),
  }));

  return orderedChecksWithOrderedQuestions.map((check, index) => ({
    ...check,
    _groupedQuestionResults: groupQuestionResults(check.question_items),
    _isLastCheck: index === otherChecks.length - 1,
    _isTyreCheck: check.question_items.some(q => q.tyre_id),
    _order: index + 1,
  }));
};

const sortInterventions = interventions =>
  interventions.sort((a, b) => {
    if (a.is_local !== b.is_local) return a.is_local ? 1 : -1;
    if (a.created_on === b.created_on) return a.title.localeCompare(b.title);
    return a.created_on.localeCompare(b.created_on);
  });

const addImageLabels = (interventions, checks) => {
  let currentImagesLabelIdx = 0;

  const setLabels = images => {
    if (!Array.isArray(images) || !images.length) return images;

    const currentLabelChar = String.fromCharCode(65 + currentImagesLabelIdx);
    let imageIndex = 0;
    let hasImages = false;

    const labeledImages = images.map(image => {
      hasImages = true;
      imageIndex += 1;

      return { ...image, label: `${currentLabelChar}${imageIndex}` };
    });

    if (hasImages) currentImagesLabelIdx += 1;

    return labeledImages;
  };

  const interventionsWithUpdatedImages = interventions.map(intervention => {
    if (!intervention.attachments?.length) return intervention;

    return {
      ...intervention,
      images: setLabels(intervention.attachments?.filter(attachment => attachment?.url?.match(/\.(jpeg|jpg|gif|png)/i) && attachment.visible_to_customer)),
    };
  });

  const checksWithUpdatedImages = checks?.map(check => {
    if (!check.question_items) return check;

    const updatedGroupedQuestionResults = new Map(
      Array.from(check._groupedQuestionResults, ([key, questionItems]) => [
        key,
        [
          ...questionItems.map(questionItem => ({
            ...questionItem,
            images: questionItem.images?.length ? setLabels(questionItem.images.filter(image => image.active)) : null,
          })),
        ],
      ])
    );

    return {
      ...check,
      _groupedQuestionResults: updatedGroupedQuestionResults,
    };
  });

  return {
    interventionsWithUpdatedImages,
    checksWithUpdatedImages,
  };
};

export const normalizeAppointmentData = appointment => {
  const { interventions = [], checks } = appointment;

  const sortedInterventions = sortInterventions(interventions);
  const orderedAndGroupedChecks = orderAndGroupChecks(checks);

  const { interventionsWithUpdatedImages: normalizedInterventions, checksWithUpdatedImages: normalizedChecks } = addImageLabels(
    sortedInterventions,
    orderedAndGroupedChecks
  );

  return {
    ...appointment,
    interventions: normalizedInterventions,
    checks: normalizedChecks,
  };
};

export const getAllImages = appointment => {
  const allImages = [];

  appointment.interventions.forEach(intervention => {
    if (intervention.images?.length) allImages.push(...intervention.images);
  });

  appointment.checks.forEach(check => {
    const groupedQuestionResultsArray = Array.from(check._groupedQuestionResults);
    groupedQuestionResultsArray.forEach(([_key, questionItems]) => {
      if (questionItems) {
        questionItems.forEach(questionItem => {
          if (questionItem.images?.length) allImages.push(...questionItem.images);
        });
      }
    });
  });

  return allImages;
};

export const getImageLabel = images => {
  if (!images?.length) return null;

  return images.reduce((acc, img, index) => {
    const separator = index === images.length - 1 ? "" : ",";
    return acc + img.label + separator;
  }, "");
};

export const isDateValid = value => value && !value.startsWith("0001-01-01T00:00:00");

export const getPrice = (price, dealerLocation) => {
  if (!(price > 0.01)) return null;
  const finalPrice = dealerLocation?.include_vat ? price * (1 + dealerLocation.vat / 100) : price;
  return round2(finalPrice).toFixed(2);
};
